import type { Editor } from '@tiptap/react';

import { useState, useCallback } from 'react';

import Box from '@mui/material/Box';
import Popper from '@mui/material/Popper';
import Tooltip from '@mui/material/Tooltip';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import { styled } from '@mui/material/styles';

import { editorClasses } from '../classes';
import { ToolbarItem } from './toolbar-item';

// ----------------------------------------------------------------------

type ColorPickerProps = {
  icon: React.ReactNode;
  editor: Editor;
  type: 'text' | 'highlight';
  active?: boolean;
  'aria-label'?: string;
  className?: string;
};

const DEFAULT_COLORS = [
  '#000000', // Black
  '#FFFFFF', // White
  '#FF0000', // Red
  '#00FF00', // Green
  '#0000FF', // Blue
  '#FFFF00', // Yellow
  '#FF00FF', // Magenta
  '#00FFFF', // Cyan
  '#FFA500', // Orange
  '#800080', // Purple
  '#FFC0CB', // Pink
  '#A52A2A', // Brown
  '#808080', // Gray
  '#C0C0C0', // Silver
  '#008000', // Dark Green
  '#000080', // Navy
];

const HIGHLIGHT_COLORS = [
  '#FFFF00', // Yellow
  '#00FF00', // Green
  '#00FFFF', // Cyan
  '#FF00FF', // Magenta
  '#FFA500', // Orange
  '#FFC0CB', // Pink
  '#90EE90', // Light Green
  '#87CEEB', // Sky Blue
  '#DDA0DD', // Plum
  '#F0E68C', // Khaki
  '#FFB6C1', // Light Pink
  '#98FB98', // Pale Green
];

export function ColorPicker({
  icon,
  editor,
  type,
  active,
  className,
  'aria-label': ariaLabel,
}: ColorPickerProps) {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  const colors = type === 'highlight' ? HIGHLIGHT_COLORS : DEFAULT_COLORS;

  const handleToggle = useCallback((event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(anchorEl ? null : event.currentTarget);
  }, [anchorEl]);

  const handleClose = useCallback(() => {
    setAnchorEl(null);
  }, []);

  const handleColorSelect = useCallback((color: string) => {
    if (type === 'text') {
      editor.chain().focus().setColor(color).run();
    } else {
      editor.chain().focus().setHighlight({ color }).run();
    }
    handleClose();
  }, [editor, type, handleClose]);

  const handleRemoveColor = useCallback(() => {
    if (type === 'text') {
      editor.chain().focus().unsetColor().run();
    } else {
      editor.chain().focus().unsetHighlight().run();
    }
    handleClose();
  }, [editor, type, handleClose]);

  const open = Boolean(anchorEl);

  return (
    <>
      <ToolbarItem
        aria-label={ariaLabel}
        active={active}
        className={className}
        onClick={handleToggle}
        icon={icon}
      />
      
      <Popper
        open={open}
        anchorEl={anchorEl}
        placement="bottom-start"
        sx={{ zIndex: 1300 }}
      >
        <ClickAwayListener onClickAway={handleClose}>
          <ColorPickerRoot>
            <ColorGrid>
              {colors.map((color) => (
                <ColorSwatch
                  key={color}
                  onClick={() => handleColorSelect(color)}
                  sx={{
                    backgroundColor: color,
                    border: color === '#FFFFFF' ? '1px solid #ccc' : 'none',
                  }}
                >
                  <Tooltip title={color} placement="top">
                    <Box sx={{ width: '100%', height: '100%' }} />
                  </Tooltip>
                </ColorSwatch>
              ))}
            </ColorGrid>
            
            <RemoveColorButton
              onClick={handleRemoveColor}
              className={editorClasses.toolbar.clear}
            >
              Remove {type === 'text' ? 'Color' : 'Highlight'}
            </RemoveColorButton>
          </ColorPickerRoot>
        </ClickAwayListener>
      </Popper>
    </>
  );
}

// ----------------------------------------------------------------------

const ColorPickerRoot = styled('div')(({ theme }) => ({
  padding: theme.spacing(1),
  borderRadius: theme.shape.borderRadius,
  boxShadow: theme.shadows[8],
  backgroundColor: theme.palette.background.paper,
  border: `1px solid ${theme.palette.divider}`,
  minWidth: 200,
}));

const ColorGrid = styled('div')(({ theme }) => ({
  display: 'grid',
  gridTemplateColumns: 'repeat(4, 1fr)',
  gap: theme.spacing(0.5),
  marginBottom: theme.spacing(1),
}));

const ColorSwatch = styled('div')(({ theme }) => ({
  width: 32,
  height: 32,
  borderRadius: theme.shape.borderRadius,
  cursor: 'pointer',
  transition: theme.transitions.create(['transform', 'box-shadow']),
  '&:hover': {
    transform: 'scale(1.1)',
    boxShadow: theme.shadows[4],
  },
}));

const RemoveColorButton = styled('button')(({ theme }) => ({
  width: '100%',
  padding: theme.spacing(0.75),
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: theme.shape.borderRadius,
  backgroundColor: 'transparent',
  color: theme.palette.text.secondary,
  fontSize: theme.typography.body2.fontSize,
  cursor: 'pointer',
  transition: theme.transitions.create(['background-color', 'color']),
  '&:hover': {
    backgroundColor: theme.palette.action.hover,
    color: theme.palette.text.primary,
  },
}));